import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import Aide from './Aide';
import { AIDES_ADHERENTS } from '../utils/aides';
import {
  ajouterLignePreset,
  fetchLignesRetirees,
  fetchReperesCourrier,
  modifierLignePreset,
  remettreLignePreset,
  retirerLignePreset,
  type CategoriePreset,
  type ChampPreset,
  type CollectionPreset,
  type DocumentPreset,
  type EvenementPreset,
  type LignePreset,
  type PresetClub,
} from '../services/api';

/**
 * Le club réécrit son préréglage : renommer, ajouter, retirer.
 *
 * POURQUOI CET ÉCRAN EXISTE
 * ---------------------------------------------------------------------------
 * Un préréglage livré tel quel ne convient à personne tout à fait. Un club de
 * football n'a pas d'équipe vétérans ; un club de judo appelle « éveil » ce que
 * la fédération nomme « baby judo » ; un club de pétanque ajoute une catégorie
 * « Doublette mixte » que le catalogue ignore.
 *
 * Sans cet écran, le préréglage était à prendre ou à laisser, et un club dont
 * un détail ne convenait pas repartait tenir ses catégories dans un tableur —
 * exactement ce que le préréglage devait lui éviter.
 *
 * CE QUE L'ÉCRAN DIT DE LUI-MÊME
 * ---------------------------------------------------------------------------
 * Deux choses, et il les dit en toutes lettres parce qu'un responsable qui
 * craint de perdre son travail ne clique pas :
 *
 * 1. Ce qu'on modifie devient sien, et aucune reprise du préréglage ne le
 *    réécrira. C'est le sens de la pastille « à vous ».
 * 2. Ce qu'on retire ne s'efface pas. La ligne quitte les écrans, et se
 *    retrouve en bas, sous « ce que vous avez retiré », d'où elle se remet.
 */
export default function ReglagesPreset({ preset }: { preset: PresetClub }) {
  const client = useQueryClient();
  const [ouverte, setOuverte] = useState<CollectionPreset | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const retirees = useQuery({
    queryKey: ['preset-retirees', preset.id],
    queryFn: () => fetchLignesRetirees(preset.id),
  });

  /*
   * Un seul point de rafraîchissement.
   *
   * Le préréglage nourrit l'écran des adhérents, le glossaire et la
   * configuration du registre. Rafraîchir l'un sans les autres laisse une
   * catégorie renommée dans un écran et pas dans le suivant, et l'on croit à
   * un enregistrement à moitié fait.
   */
  const rafraichir = () => {
    void client.invalidateQueries({ queryKey: ['preset-club'] });
    void client.invalidateQueries({ queryKey: ['preset-retirees'] });
    void client.invalidateQueries({ queryKey: ['glossaire-sportif'] });
    void client.invalidateQueries({ queryKey: ['adherents-configuration'] });
  };

  /* Le serveur refuse en disant pourquoi ; on le répète tel quel. Un message
     écrit pour le trésorier vaut mieux qu'un « une erreur est survenue » qui
     l'oblige à deviner ce qu'il a mal saisi. */
  const surReponse = (reponse: { success?: boolean; message?: string }) => {
    if (reponse?.success === false) {
      setErreur(reponse.message ?? 'La modification a été refusée.');
      return false;
    }
    setErreur(null);
    rafraichir();
    return true;
  };

  const modification = useMutation({
    mutationFn: ({
      collection,
      id,
      valeurs,
    }: {
      collection: CollectionPreset;
      id: number;
      valeurs: Record<string, unknown>;
    }) => modifierLignePreset(collection, id, valeurs),
    onSuccess: surReponse,
    onError: (e: Error) => setErreur(e.message),
  });

  const ajout = useMutation({
    mutationFn: ({
      collection,
      valeurs,
    }: {
      collection: CollectionPreset;
      valeurs: Record<string, unknown>;
    }) => ajouterLignePreset(collection, valeurs, preset.id),
    onSuccess: surReponse,
    onError: (e: Error) => setErreur(e.message),
  });

  const retrait = useMutation({
    mutationFn: ({ collection, id }: { collection: CollectionPreset; id: number }) =>
      retirerLignePreset(collection, id),
    onSuccess: surReponse,
    onError: (e: Error) => setErreur(e.message),
  });

  const remise = useMutation({
    mutationFn: ({ collection, id }: { collection: CollectionPreset; id: number }) =>
      remettreLignePreset(collection, id),
    onSuccess: surReponse,
    onError: (e: Error) => setErreur(e.message),
  });

  const enCours =
    modification.isPending || ajout.isPending || retrait.isPending || remise.isPending;

  /*
   * Les postes et les fonctions d'encadrement sont une seule collection côté
   * serveur — « roles » — séparée par une famille. L'écran, lui, les montre
   * séparément : « avant-centre » et « éducateur diplômé » ne se cherchent pas
   * dans la même liste.
   */
  const sections: Array<{
    cle: string;
    collection: CollectionPreset;
    titre: string;
    aide: string;
    lignes: LignePreset[];
    nommer: (ligne: never) => string;
    detail: (ligne: never) => string;
    /** Ce qu'on envoie au serveur pour créer une ligne vierge de ce type. */
    modele: Record<string, unknown>;
    /** Le champ qui porte le nom, celui qu'on modifie d'un clic. */
    champNom: string;
    /**
     * Faut-il montrer la liste des repères sous cette section ?
     *
     * Vraie pour les seuls courriers types. Les poser en tête de l'écran les
     * ferait lire par tous les clubs qui viennent renommer une catégorie —
     * donc ignorer par ceux qui en auraient besoin.
     */
    aideReperes?: boolean;
  }> = [
    {
      cle: 'categories',
      collection: 'categories',
      titre: 'Catégories',
      aide: 'Les tranches d’âge de votre fédération. Le libellé décide du rangement : « 12-13 ans » range les licenciés de 12 et 13 ans.',
      lignes: preset.categories,
      nommer: (c: CategoriePreset) => c.code,
      detail: (c: CategoriePreset) =>
        [c.ageLibelle, c.effectif, c.niveau].filter(Boolean).join(' · '),
      modele: { code: 'Nouvelle catégorie', ageLibelle: 'toutes catégories' },
      champNom: 'code',
    },
    {
      cle: 'postes',
      collection: 'roles',
      titre: 'Postes',
      aide: 'Ce qu’un licencié occupe sur le terrain.',
      lignes: preset.postes,
      nommer: (r: { libelle: string }) => r.libelle,
      detail: () => '',
      modele: { libelle: 'Nouveau poste', famille: 'poste' },
      champNom: 'libelle',
    },
    {
      cle: 'encadrants',
      collection: 'roles',
      titre: 'Fonctions d’encadrement',
      aide: 'Entraîneur, arbitre, dirigeant : ce qu’une personne fait autour du terrain.',
      lignes: preset.encadrants,
      nommer: (r: { libelle: string }) => r.libelle,
      detail: () => '',
      modele: { libelle: 'Nouvelle fonction', famille: 'encadrant' },
      champNom: 'libelle',
    },
    {
      cle: 'champs',
      collection: 'champs',
      titre: 'Champs de la fiche',
      aide: 'Ce que la fiche d’un licencié demande en plus de l’état civil.',
      lignes: preset.champs,
      nommer: (c: ChampPreset) => c.libelle || c.code,
      detail: (c: ChampPreset) =>
        [c.type, c.obligatoire ? 'obligatoire' : '', c.valeurs.length > 0 ? `${c.valeurs.length} valeurs` : '']
          .filter(Boolean)
          .join(' · '),
      modele: { code: 'nouveau_champ', libelle: 'Nouveau champ', type: 'texte' },
      champNom: 'libelle',
    },
    {
      cle: 'evenements',
      collection: 'evenements',
      titre: 'Modèles d’événement',
      aide: 'Entraînement, match, tournoi : ce que votre planning propose d’un clic.',
      lignes: preset.evenements,
      nommer: (e: EvenementPreset) => e.libelle,
      detail: (e: EvenementPreset) =>
        [e.recurrence, e.dureeMinutes > 0 ? `${e.dureeMinutes} min` : ''].filter(Boolean).join(' · '),
      modele: { libelle: 'Nouvel événement', recurrence: 'ponctuelle', dureeMinutes: 90 },
      champNom: 'libelle',
    },
    {
      cle: 'documents',
      collection: 'documents',
      titre: 'Courriers types',
      aide: 'Les messages que vous envoyez souvent. Les repères entre accolades sont remplis avec votre club, votre fédération et le licencié concerné.',
      lignes: preset.documents,
      nommer: (d: DocumentPreset) => d.libelle || d.code,
      detail: (d: DocumentPreset) => d.sujet,
      modele: { code: 'nouveau_courrier', libelle: 'Nouveau courrier', sujet: '', corps: '' },
      champNom: 'libelle',
      /*
        Les repères ne s'affichent que sous les courriers types.

        C'est le seul endroit où ils servent, et les poser en tête de l'écran
        les ferait lire par tous les clubs qui viennent renommer une catégorie
        — donc ignorer par ceux qui en auraient besoin.
      */
      aideReperes: true,
    },
  ];

  const listeRetirees = retirees.data?.retirees ?? [];

  return (
    <section className="space-y-3">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {/*
              Le titre nomme la discipline.

              Un club omnisports voit trois de ces panneaux à la suite. « Adapter
              à votre club » trois fois de suite ne dit pas lequel on modifie —
              et on renomme alors une catégorie de gymnastique en croyant
              toucher au football.
            */}
            Adapter {preset.nom} à votre club
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Renommez, ajoutez, retirez. Ce que vous modifiez porte la mention{' '}
            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              à vous
            </span>{' '}
            et ne sera jamais réécrit par une mise à jour de votre fédération.
          </p>
        </div>
      </header>

      {erreur !== null && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 px-3 py-2 text-sm text-red-800 dark:text-red-300 flex items-start justify-between gap-2"
        >
          <span>{erreur}</span>
          <button
            type="button"
            onClick={() => setErreur(null)}
            aria-label="Masquer ce message"
            className="shrink-0 text-red-600 hover:text-red-800 dark:text-red-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-2">
        {sections.map((section) => (
          <Section
            key={section.cle}
            titre={section.titre}
            aide={section.aide}
            nombre={section.lignes.length}
            ouverte={ouverte === (section.cle as CollectionPreset)}
            surBascule={() =>
              setOuverte(
                ouverte === (section.cle as CollectionPreset)
                  ? null
                  : (section.cle as CollectionPreset),
              )
            }
          >
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {section.lignes.map((ligne) => (
                <Ligne
                  key={ligne.id}
                  nom={section.nommer(ligne as never)}
                  detail={section.detail(ligne as never)}
                  sienne={!ligne.fourni}
                  occupe={enCours}
                  surRenommer={(nom) =>
                    modification.mutate({
                      collection: section.collection,
                      id: ligne.id,
                      valeurs: { [section.champNom]: nom },
                    })
                  }
                  surRetirer={() =>
                    retrait.mutate({ collection: section.collection, id: ligne.id })
                  }
                />
              ))}

              {section.lignes.length === 0 && (
                <li className="py-3 text-sm text-gray-500 dark:text-gray-400">
                  Rien pour l’instant.
                </li>
              )}
            </ul>

            <button
              type="button"
              disabled={enCours}
              onClick={() => ajout.mutate({ collection: section.collection, valeurs: section.modele })}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 dark:text-primary-400 hover:underline disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>

            {section.aideReperes === true && (
              <>
                {AIDES_ADHERENTS['Courriers types'] !== undefined && (
                  <div className="mt-3">
                    <Aide titre="Courriers types">{AIDES_ADHERENTS['Courriers types']}</Aide>
                  </div>
                )}
                <AideReperes />
              </>
            )}
          </Section>
        ))}
      </div>

      {/*
        Ce qui a été retiré, et où le retrouver.

        Sans cette liste, une ligne retirée serait introuvable : elle ne
        s'affiche nulle part ailleurs, c'est tout son objet. Une suppression
        dont on ne peut pas revenir demande une confirmation ; une suppression
        dont on peut revenir demande seulement de savoir où regarder.
      */}
      {listeRetirees.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
            Ce que vous avez retiré
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Rien n’a été effacé. Une ligne retirée ne revient pas quand vous reprenez le préréglage
            de votre fédération — c’est bien une décision, pas un délai.
          </p>
          <ul className="mt-2 space-y-1">
            {listeRetirees.map((r) => (
              <li
                key={`${r.collection}-${r.id}`}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="text-gray-700 dark:text-gray-300 truncate">
                  {r.libelle}
                  <span className="text-gray-400 dark:text-gray-500"> · {r.collection}</span>
                </span>
                <button
                  type="button"
                  disabled={enCours}
                  onClick={() => remise.mutate({ collection: r.collection, id: r.id })}
                  className="shrink-0 inline-flex items-center gap-1 text-primary-700 dark:text-primary-400 hover:underline disabled:opacity-50"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Remettre
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** Une section repliable. Une seule ouverte à la fois : la liste est longue. */
function Section({
  titre,
  aide,
  nombre,
  ouverte,
  surBascule,
  children,
}: {
  titre: string;
  aide: string;
  nombre: number;
  ouverte: boolean;
  surBascule: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800">
      <button
        type="button"
        onClick={surBascule}
        aria-expanded={ouverte}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left min-h-[44px]"
      >
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {titre}
          <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
            {nombre}
          </span>
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${ouverte ? 'rotate-180' : ''}`}
        />
      </button>

      {ouverte && (
        <div className="px-3 pb-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {aide}
          </p>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Une ligne, et son renommage sur place.
 *
 * Le renommage se fait dans la ligne plutôt que dans une fenêtre : changer
 * « Baby judo » en « Éveil » est le geste le plus fréquent de cet écran, et
 * lui demander deux clics et un dialogue le rendrait pénible au point qu'on ne
 * le ferait plus.
 */
function Ligne({
  nom,
  detail,
  sienne,
  occupe,
  surRenommer,
  surRetirer,
}: {
  nom: string;
  detail: string;
  sienne: boolean;
  occupe: boolean;
  surRenommer: (nom: string) => void;
  surRetirer: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [saisie, setSaisie] = useState(nom);

  const valider = () => {
    const propre = saisie.trim();
    setEdition(false);
    // Rien n'a changé : pas d'appel. Un enregistrement pour rien poserait
    // « à vous » sur une ligne que le club n'a pas touchée, et la
    // soustrairait aux corrections de sa fédération sans qu'il l'ait voulu.
    if (propre !== '' && propre !== nom) {
      surRenommer(propre);
    } else {
      setSaisie(nom);
    }
  };

  return (
    <li className="py-2 flex items-center gap-2">
      {edition ? (
        <input
          autoFocus
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onBlur={valider}
          onKeyDown={(e) => {
            if (e.key === 'Enter') valider();
            if (e.key === 'Escape') {
              setSaisie(nom);
              setEdition(false);
            }
          }}
          aria-label={`Renommer « ${nom} »`}
          className="flex-1 min-w-0 rounded border border-primary-300 dark:border-primary-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEdition(true)}
          className="flex-1 min-w-0 text-left group"
        >
          <span className="text-sm text-gray-900 dark:text-gray-100 group-hover:underline">
            {nom}
          </span>
          {detail !== '' && (
            <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
              {detail}
            </span>
          )}
        </button>
      )}

      {sienne && (
        <span
          title="Modifiée par vous : une reprise du préréglage ne la réécrira pas."
          className="shrink-0 rounded px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
        >
          à vous
        </span>
      )}

      {!edition && (
        <button
          type="button"
          onClick={() => setEdition(true)}
          aria-label={`Renommer « ${nom} »`}
          className="shrink-0 inline-flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 text-gray-400 hover:text-primary-600"
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}

      <button
        type="button"
        disabled={occupe}
        onClick={surRetirer}
        aria-label={`Retirer « ${nom} »`}
        title="Retirer — la ligne est conservée et se remet en un clic."
        className="shrink-0 inline-flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 text-gray-400 hover:text-red-600 disabled:opacity-40"
      >
        {occupe ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      </button>
    </li>
  );
}

/**
 * Les repères qu'un courrier type peut employer.
 *
 * POURQUOI CETTE LISTE EST NÉCESSAIRE
 * ---------------------------------------------------------------------------
 * L'écran annonçait « les repères entre accolades sont remplis » sans dire
 * LESQUELS. Le club les devinait, en écrivait un qui n'existe pas, et
 * l'enregistrement le refusait — après trois paragraphes rédigés.
 *
 * L'EXEMPLE VAUT MIEUX QUE LE NOM
 * ---------------------------------------------------------------------------
 * « discipline.immatriculation » ne dit pas grand-chose ; « AFF-FOO-1042 » le
 * dit d'un coup. Quand la valeur est vide, on montre le repère seul — c'est
 * aussi une information : il faut d'abord remplir la fiche de la fédération.
 *
 * ELLE RESTE REPLIÉE
 * ---------------------------------------------------------------------------
 * Une vingtaine de repères déployés sous chaque courrier repousserait le
 * bouton « Ajouter » hors de l'écran. On l'ouvre quand on écrit, c'est-à-dire
 * rarement.
 */
function AideReperes() {
  const reperes = useQuery({ queryKey: ['reperes-courrier'], queryFn: fetchReperesCourrier });

  const liste = reperes.data?.reperes ?? [];

  if (liste.length === 0) {
    return null;
  }

  const familles = [...new Set(liste.map((r) => r.famille))];

  return (
    <details className="mt-3 rounded-lg border border-gray-200 dark:border-gray-800 p-3">
      <summary className="cursor-pointer text-sm font-medium text-gray-800 dark:text-gray-200">
        Les repères que vous pouvez employer ({liste.length})
      </summary>

      <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
        Écrivez-les entre doubles accolades, par exemple{' '}
        <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">{'{{ adherent.prenom }}'}</code>.
        Ils sont remplacés au moment où le courrier est produit, avec les éléments de votre club, de
        votre fédération et du licencié concerné.
      </p>

      {familles.map((famille) => (
        <div key={famille} className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {famille}
          </p>
          <ul className="mt-1 grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {liste
              .filter((r) => r.famille === famille)
              .map((r) => (
                <li key={r.repere} className="flex items-baseline justify-between gap-3 text-xs">
                  <code className="text-gray-800 dark:text-gray-200">{'{{ ' + r.repere + ' }}'}</code>
                  {r.exemple !== '' && (
                    <span className="truncate text-gray-500 dark:text-gray-400">{r.exemple}</span>
                  )}
                </li>
              ))}
          </ul>
        </div>
      ))}
    </details>
  );
}
